/* Lumeva — buy box, size guide, sticky add-to-cart, section reveal.
   Vanilla, no dependencies, deferred. Everything degrades: with JS off the
   product form falls back to a native <select> and all content is visible. */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     Money — formatted against the shop's own money_format so the button
     total always reads exactly the way checkout will.
     ---------------------------------------------------------------------- */

  function formatMoney(cents, format) {
    function group(number, decimals, thousands, decimalSep) {
      var n = (cents / 100).toFixed(decimals);
      var parts = n.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimalSep + parts[1] : parts[0];
    }

    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount':
          return group(cents, 2, ',', '.');
        case 'amount_no_decimals':
          return group(cents, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(cents, 0, '.', ',');
        default:
          return group(cents, 2, ',', '.');
      }
    });
  }

  /* ----------------------------------------------------------------------
     Buy box — two radio groups (size, pack) resolve to one variant.
     ---------------------------------------------------------------------- */

  // Every [data-lum-buy] instance on the page registers its updater here. The
  // buy box and the final CTA render the same selectors, so a change in either
  // has to be a change in both — it is one choice made in two places.
  var boxes = [];

  function initBuyBox(root) {
    var dataEl = root.querySelector('[data-lum-variants]');
    if (!dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      return; // leave the server-rendered default in place
    }

    var input = root.querySelector('[data-lum-variant-input]');
    var atcText = root.querySelector('[data-lum-atc-text]');
    var atcBtn = root.querySelector('[data-lum-atc]');
    var atcLabel = atcText ? atcText.textContent.split('—')[0].trim() : 'Add to cart';
    var sizes = root.querySelectorAll('[data-lum-size]');
    var packs = root.querySelectorAll('[data-lum-pack]');

    function selected(list) {
      for (var i = 0; i < list.length; i++) {
        if (list[i].checked) return list[i].value;
      }
      return null;
    }

    function update() {
      var size = selected(sizes);
      var pack = selected(packs);

      var match = null;
      for (var i = 0; i < data.variants.length; i++) {
        var v = data.variants[i];
        if ((!size || v.size === size) && (!pack || v.pack === pack)) {
          match = v;
          break;
        }
      }
      if (!match) return;

      if (input) input.value = match.id;

      if (atcText) {
        atcText.innerHTML =
          atcLabel +
          ' — <span class="lum-data">' +
          formatMoney(match.price, data.moneyFormat) +
          '</span>';
      }

      // Inventory is untracked and set to keep selling — this only ever fires
      // if that changes. No fake scarcity is implied by it.
      if (atcBtn && match.available === false) {
        atcBtn.disabled = true;
        if (atcText) atcText.textContent = 'Unavailable';
      } else if (atcBtn) {
        atcBtn.disabled = false;
      }

      // Reflect the selection on the labels, including the measurement line
      // that marks the chosen size.
      [sizes, packs].forEach(function (list) {
        for (var i = 0; i < list.length; i++) {
          var label = list[i].closest('label');
          if (label) label.classList.toggle('is-sel', list[i].checked);
        }
      });

      root.dispatchEvent(new CustomEvent('lumeva:variant', { detail: match, bubbles: true }));
    }

    // Adopt a selection made in another instance without re-broadcasting it.
    function adopt(size, pack) {
      function set(list, value) {
        for (var i = 0; i < list.length; i++) list[i].checked = list[i].value === value;
      }
      if (size) set(sizes, size);
      if (pack) set(packs, pack);
      update();
    }

    root.addEventListener('change', function (e) {
      if (!e.target.matches('[data-lum-size], [data-lum-pack]')) return;
      update();

      var size = selected(sizes);
      var pack = selected(packs);
      boxes.forEach(function (box) {
        if (box.root !== root) box.adopt(size, pack);
      });
    });

    boxes.push({ root: root, adopt: adopt });
    update();
  }

  /* ----------------------------------------------------------------------
     Gallery
     ---------------------------------------------------------------------- */

  function initGallery(root) {
    var main = root.querySelector('.lum-gallery__img');
    var thumbs = root.querySelectorAll('[data-lum-thumb]');
    if (!main || !thumbs.length) return;

    root.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-lum-thumb]');
      if (!thumb) return;

      // Swap the whole responsive set, not just src. Dropping srcset made every
      // thumbnail click download the 1200w file on a phone.
      var srcset = thumb.getAttribute('data-lum-thumb-srcset');
      main.src = thumb.getAttribute('data-lum-thumb');
      if (srcset) main.srcset = srcset;
      else main.removeAttribute('srcset');

      var alt = thumb.getAttribute('data-lum-thumb-alt');
      if (alt) main.alt = alt;

      for (var i = 0; i < thumbs.length; i++) {
        thumbs[i].classList.remove('is-sel');
        thumbs[i].removeAttribute('aria-current');
      }
      thumb.classList.add('is-sel');
      thumb.setAttribute('aria-current', 'true');
    });
  }

  /* ----------------------------------------------------------------------
     FAQ — <details> carries its own expanded state, but the attribute is
     mirrored onto the summary so assistive technology and any automated
     accessibility check see it explicitly. Content stays readable with JS off,
     which a button-driven accordion could not promise.
     ---------------------------------------------------------------------- */

  function initFaq() {
    var items = document.querySelectorAll('.lum-faq__item');
    for (var i = 0; i < items.length; i++) {
      (function (item) {
        var summary = item.querySelector('summary');
        if (!summary) return;
        summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');
        item.addEventListener('toggle', function () {
          summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');
        });
      })(items[i]);
    }
  }

  /* ----------------------------------------------------------------------
     Product demonstration video — plays only while on screen, never with
     sound, and never at all under prefers-reduced-motion.
     ---------------------------------------------------------------------- */

  function initMotion() {
    var videos = document.querySelectorAll('.lum-motion__video');
    if (!videos.length) return;

    for (var i = 0; i < videos.length; i++) {
      var v = videos[i];
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      // A poster with no controls and no autoplay is a still image to anyone
      // who has asked for less motion. Give them the controls instead.
      if (reduceMotion) v.controls = true;
    }

    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            var played = video.play();
            if (played && played.catch) played.catch(function () {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    for (var j = 0; j < videos.length; j++) io.observe(videos[j]);
  }

  /* ----------------------------------------------------------------------
     Size guide — native <dialog> so focus trapping and Escape come free.
     ---------------------------------------------------------------------- */

  var sizeGuideWired = false;

  function initSizeGuide() {
    var dialog = document.querySelector('[data-lum-size-guide]');
    if (!dialog || sizeGuideWired) return;
    sizeGuideWired = true;

    document.addEventListener('click', function (e) {
      dialog = document.querySelector('[data-lum-size-guide]');
      if (!dialog) return;

      if (e.target.closest('[data-lum-open-size-guide]')) {
        e.preventDefault();
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }

      if (e.target.closest('[data-lum-close-size-guide]')) {
        dialog.close();
      }

      // Click on the backdrop closes it.
      if (e.target === dialog) dialog.close();
    });
  }

  /* ----------------------------------------------------------------------
     Sticky add-to-cart — appears once the buy box has scrolled out of view.
     ---------------------------------------------------------------------- */

  var stickyWired = false;

  function initSticky() {
    // Two surfaces, one behaviour: the compact button in the header (desktop)
    // and the bottom bar (mobile). CSS decides which of them can be seen; this
    // keeps both in step with the buy box so neither can post a stale variant.
    var bars = document.querySelectorAll('[data-lum-sticky], [data-lum-bar]');

    // Watch the real button, not the whole section. Anchored to the section it
    // only appeared after the entire buy box had gone by, which on a phone is
    // two and a half screens of scrolling with no button reachable.
    var buy = document.querySelector('[data-lum-atc]') ||
              document.querySelector('[data-lum-sticky-anchor]');
    if (!bars.length || !buy || stickyWired || !('IntersectionObserver' in window)) return;
    stickyWired = true;

    // The bottom bar starts [hidden] so it can never flash over the fold on a
    // slow first paint. It becomes a candidate for display only once we know
    // the real button's position.
    var bottom = document.querySelector('[data-lum-bar]');

    // Visible whenever the real button is not on screen — above or below. That
    // means a shopper at the top of the page has a one-tap path to the cart
    // instead of scrolling to find one, and the two buttons are never both
    // visible at once.
    new IntersectionObserver(
      function (entries) {
        var away = !entries[0].isIntersecting;
        if (bottom) bottom.hidden = false;
        for (var i = 0; i < bars.length; i++) bars[i].classList.toggle('is-in', away);
      },
      { threshold: 0 }
    ).observe(buy);

    // Keep every sticky surface's variant, price, size and pack in step with
    // the buy box. No cart logic is duplicated — these forms post the same
    // variant id to the same endpoint, and the same submit handler that opens
    // the drawer picks them up.
    document.addEventListener('lumeva:variant', function (e) {
      var fmt = '${{amount}}';
      var dataEl = document.querySelector('[data-lum-variants]');
      try {
        fmt = JSON.parse(dataEl.textContent).moneyFormat;
      } catch (err) {}

      for (var i = 0; i < bars.length; i++) {
        var bar = bars[i];
        var input = bar.querySelector('[data-lum-sticky-variant]');
        var price = bar.querySelector('[data-lum-sticky-price]');
        var size = bar.querySelector('[data-lum-bar-size]');
        var pack = bar.querySelector('[data-lum-bar-pack]');

        if (input) input.value = e.detail.id;
        if (price) price.textContent = formatMoney(e.detail.price, fmt);
        if (size && e.detail.size) size.textContent = e.detail.size;
        if (pack && e.detail.pack) pack.textContent = e.detail.pack;
      }
    });
  }

  /* ----------------------------------------------------------------------
     Reveal — a fade-and-rise on section entry, 300ms, and nothing else.
     ---------------------------------------------------------------------- */

  function initReveal() {
    var items = document.querySelectorAll('.lum-reveal');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      for (var i = 0; i < items.length; i++) items[i].classList.add('is-in');
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    for (var j = 0; j < items.length; j++) io.observe(items[j]);
  }


  /* ----------------------------------------------------------------------
     Cart drawer.

     Adding to cart shouldn't cost the shopper their place on the page, so the
     form is intercepted and the drawer opens over it. The drawer's markup is
     re-rendered by Shopify (Section Rendering API) rather than assembled here,
     which keeps money formatting, discounts and currency correct without
     duplicating any of that in JavaScript.

     Every failure path falls back to a normal form post, so a network error or
     an unsupported browser still ends up on the real /cart page.
     ---------------------------------------------------------------------- */

  var SECTION = 'lum-cart-drawer';
  var cartWired = false;
  var lastFocus = null;

  function drawer() {
    return document.querySelector('[data-lum-cart]');
  }

  function setBusy(form, busy) {
    var btn = form && form.querySelector('[data-lum-atc]');
    if (!btn) return;
    btn.disabled = busy;
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function openCart() {
    var el = drawer();
    if (!el) return;
    lastFocus = document.activeElement;
    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');

    // Force a style/layout flush so the transition still has a closed state to
    // run from, then open synchronously. This used to sit in a
    // requestAnimationFrame; when that frame didn't arrive the drawer was left
    // un-hidden but never opened — an invisible full-screen scrim over the page
    // swallowing clicks, with the panel parked off-screen.
    void el.offsetHeight;

    el.classList.add('is-open');
    document.documentElement.classList.add('lum-noscroll');

    // The scrim also carries data-lum-cart-close but is a div, so focusing it
    // silently did nothing and left focus outside the modal.
    var focusable = el.querySelector('.lum-cart__close') ||
                    el.querySelector('button, a[href]');
    if (focusable) focusable.focus();
  }

  function closeCart() {
    var el = drawer();
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lum-noscroll');
    window.setTimeout(function () {
      if (!el.classList.contains('is-open')) el.hidden = true;
    }, 300);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function swap(html) {
    if (!html) return;
    var wrap = document.getElementById('shopify-section-' + SECTION);
    if (!wrap) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = parsed.getElementById('shopify-section-' + SECTION) ||
                parsed.querySelector('[data-lum-cart]');
    if (!fresh) return;

    // The re-rendered markup always comes back closed, so the open state has to
    // survive the swap by hand. Without this, changing a quantity makes the
    // drawer vanish mid-interaction and leaves the scroll lock stuck on.
    var before = drawer();
    var wasOpen = !!(before && before.classList.contains('is-open'));

    wrap.innerHTML = fresh.getAttribute('data-lum-cart') !== null
      ? fresh.outerHTML
      : fresh.innerHTML;

    if (wasOpen) {
      var next = drawer();
      if (next) {
        next.hidden = false;
        next.classList.add('is-open');
        next.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('lum-noscroll');
        // The element that had focus was just removed from the document.
        var refocus = next.querySelector('.lum-cart__close');
        if (refocus) refocus.focus();
      }
    }

    // Keep the header bubble in step with the drawer.
    var count = parsed.querySelector('.lum-cart__count');
    var bubble = document.querySelector('.lum-cart__count-header');
    if (bubble) bubble.textContent = count ? count.textContent.trim() : '';
  }

  function post(url, payload) {
    payload.sections = [SECTION];
    payload.sections_url = window.location.pathname;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw data;
        return data;
      });
    });
  }

  function initCart() {
    if (cartWired || !drawer() || !window.fetch || !window.DOMParser) return;
    cartWired = true;

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      var idInput = form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) return; // let the browser handle it

      e.preventDefault();
      setBusy(form, true);

      post(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js',
           { items: [{ id: Number(idInput.value), quantity: 1 }] })
        .then(function (data) {
          swap(data.sections && data.sections[SECTION]);
          openCart();
        })
        .catch(function () {
          form.submit(); // network or inventory error — use the real cart page
        })
        .then(function () {
          setBusy(form, false);
        });
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-lum-cart-close]')) {
        e.preventDefault();
        closeCart();
        return;
      }

      // The header cart opens the drawer rather than leaving the page.
      var cartLink = e.target.closest('[data-lum-cart-open]');
      if (cartLink) {
        e.preventDefault();
        openCart();
        return;
      }

      var qty = e.target.closest('[data-lum-qty]');
      if (qty) {
        e.preventDefault();
        qty.disabled = true;
        post('/cart/change.js', {
          line: Number(qty.getAttribute('data-lum-qty')),
          quantity: Number(qty.getAttribute('data-lum-qty-to'))
        })
          .then(function (data) { swap(data.sections && data.sections[SECTION]); })
          .catch(function () { window.location.href = '/cart'; });
      }
    });

    document.addEventListener('keydown', function (e) {
      var el = drawer();
      if (!el || !el.classList.contains('is-open')) return;

      if (e.key === 'Escape') {
        closeCart();
        return;
      }

      // Keep Tab inside the panel while it is modal.
      if (e.key !== 'Tab') return;
      var panel = el.querySelector('[data-lum-cart-panel]');
      var items = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ---------------------------------------------------------------------- */

  function boot() {
    boxes = [];
    // Sticky first: it listens for lumeva:variant, and the buy box dispatches
    // that during its own init. Wired the other way round, the sticky bar would
    // miss the opening price and keep the server-rendered default.
    initSticky();
    document.querySelectorAll('[data-lum-buy]').forEach(initBuyBox);
    document.querySelectorAll('[data-lum-gallery]').forEach(initGallery);
    initSizeGuide();
    initCart();
    initReveal();
    initFaq();
    initMotion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
